// Cliente de archivos compatible con `supabase.storage`.
//
// La aplicación sólo usa upload() y getPublicUrl() sobre el bucket
// `product-images` (logo de la empresa e imágenes de productos).

import { GatewayError, request } from './transport';

// Los archivos subidos se leen por `/api/storage/public/...`. Tiene que colgar de `/api` porque
// es lo único que llega hasta el servidor de Node: nginx en producción y el proxy de Vite en
// desarrollo mandan el resto al frontend. Con el `/uploads` de antes, el logo recién subido salía
// roto aunque el archivo estuviera guardado en el servidor. Ver server/pg/static-uploads.mjs.
const PUBLIC_BASE = String(
  (import.meta as any)?.env?.VITE_STORAGE_PUBLIC_BASE_URL ?? '/api/storage/public',
).replace(/\/$/, '');

interface StorageResult<T> {
  data: T | null;
  error: { message: string; name: string; status?: number } | null;
}

const toStorageError = (err: unknown) => {
  if (err instanceof GatewayError) {
    return { name: 'StorageApiError', message: err.message, status: err.status };
  }
  return { name: 'StorageError', message: err instanceof Error ? err.message : String(err) };
};

async function toBase64(file: Blob): Promise<string> {
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);

  // Se trocea porque String.fromCharCode(...bytes) desborda la pila con
  // archivos grandes.
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

class BucketClient {
  private readonly bucket: string;

  constructor(bucket: string) {
    this.bucket = bucket;
  }

  async upload(
    path: string,
    file: Blob | File | ArrayBuffer | Uint8Array,
    options?: { cacheControl?: string; upsert?: boolean; contentType?: string },
  ): Promise<StorageResult<{ path: string; fullPath: string; id?: string }>> {
    try {
      const blob =
        file instanceof Blob
          ? file
          : new Blob([file as BlobPart], { type: options?.contentType ?? 'application/octet-stream' });

      const base64 = await toBase64(blob);

      const payload = await request<{ data: { path: string; fullPath: string; publicUrl: string } }>(
        '/storage/upload',
        {
          method: 'POST',
          body: {
            bucket: this.bucket,
            path,
            base64,
            contentType: options?.contentType || (file as File)?.type || blob.type,
            cacheControl: options?.cacheControl,
            upsert: options?.upsert ?? false,
          },
        },
      );

      return { data: payload.data, error: null };
    } catch (err) {
      return { data: null, error: toStorageError(err) };
    }
  }

  /** Síncrono, igual que en supabase-js: la URL se compone en el cliente. */
  getPublicUrl(path: string): { data: { publicUrl: string } } {
    const clean = String(path).replace(/^\/+/, '');
    return { data: { publicUrl: `${PUBLIC_BASE}/${this.bucket}/${clean}` } };
  }

  async remove(paths: string[]): Promise<StorageResult<Array<{ name: string }>>> {
    try {
      const payload = await request<{ data: Array<{ name: string }> }>('/storage/remove', {
        method: 'POST',
        body: { bucket: this.bucket, paths },
      });
      return { data: payload.data, error: null };
    } catch (err) {
      return { data: null, error: toStorageError(err) };
    }
  }
}

export class StorageClient {
  from(bucket: string) {
    return new BucketClient(bucket);
  }
}
